import type { RootState } from '~/store/store.ts';
import { initialState } from './initialState.ts';
import { GuaranteesFetchStatus } from './types.ts';
import type { TGuaranteesSupervisorGroupKey } from './types.ts';
import type { TSupervisorAdministrationPageKey } from '~/modules/supervisor-administration/types.ts';

const select = (state: RootState) => state.guarantees;
const selectPrincipalSlice = (state: RootState) =>
  select(state).principal ?? initialState.principal;
const selectPatchPrincipalSlice = (state: RootState) =>
  select(state).patchPrincipal ?? initialState.patchPrincipal;
const selectBeneficiarySlice = (state: RootState) =>
  select(state).beneficiary ?? initialState.beneficiary;
const selectPatchBeneficiarySlice = (state: RootState) =>
  select(state).patchBeneficiary ?? initialState.patchBeneficiary;
const selectBaseRelationsSlice = (state: RootState) =>
  select(state).baseRelations ?? initialState.baseRelations;
const selectPatchBaseRelationsSlice = (state: RootState) =>
  select(state).patchBaseRelations ?? initialState.patchBaseRelations;
const selectGuaranteeFormSlice = (state: RootState) =>
  select(state).guaranteeForm ?? initialState.guaranteeForm;
const selectPatchGuaranteeFormSlice = (state: RootState) =>
  select(state).patchGuaranteeForm ?? initialState.patchGuaranteeForm;
const selectMainDealConditionsSlice = (state: RootState) =>
  select(state).mainDealConditions ?? initialState.mainDealConditions;
const selectPatchMainDealConditionsSlice = (state: RootState) =>
  select(state).patchMainDealConditions ?? initialState.patchMainDealConditions;
const selectManagerTermsSlice = (state: RootState) =>
  select(state).managerTerms ?? initialState.managerTerms;
const selectPatchManagerTermsSlice = (state: RootState) =>
  select(state).patchManagerTerms ?? initialState.patchManagerTerms;
const selectExecutionFormSlice = (state: RootState) =>
  select(state).executionForm ?? initialState.executionForm;
const selectPatchExecutionFormSlice = (state: RootState) =>
  select(state).patchExecutionForm ?? initialState.patchExecutionForm;
const selectPatchIssueGuaranteeSlice = (state: RootState) =>
  select(state).patchIssueGuarantee ?? initialState.patchIssueGuarantee;
const selectApplicationTenderInfoSlice = (state: RootState) =>
  select(state).applicationTenderInfo ?? initialState.applicationTenderInfo;
const selectSubmitApplicationByManagerSlice = (state: RootState) =>
  select(state).submitApplicationByManager ?? initialState.submitApplicationByManager;
const selectSubmitApprovalLetterByClientSlice = (state: RootState) =>
  select(state).submitApprovalLetterByClient ?? initialState.submitApprovalLetterByClient;
const selectSubmitApplicationByUdbtfSlice = (state: RootState) =>
  select(state).submitApplicationByUdbtf ?? initialState.submitApplicationByUdbtf;
const selectSubmitApprovalLetterByUdbtfSlice = (state: RootState) =>
  select(state).submitApprovalLetterByUdbtf ?? initialState.submitApprovalLetterByUdbtf;
const selectSubmitApprovalLetterByUsapokSlice = (state: RootState) =>
  select(state).submitApprovalLetterByUsapok ?? initialState.submitApprovalLetterByUsapok;
const selectSubmitApplicationByUakdoSlice = (state: RootState) =>
  select(state).submitApplicationByUakdo ?? initialState.submitApplicationByUakdo;
const selectSubmitApplicationByUakdoControllerSlice = (state: RootState) =>
  select(state).submitApplicationByUakdoController ??
  initialState.submitApplicationByUakdoController;
const selectRejectApplicationByUakdoControllerSlice = (state: RootState) =>
  select(state).rejectApplicationByUakdoController ??
  initialState.rejectApplicationByUakdoController;
const selectSubmitApplicationByAmManagerSlice = (state: RootState) =>
  select(state).submitApplicationByAmManager ?? initialState.submitApplicationByAmManager;
const selectSubmitApprovalLetterByAmManagerSlice = (state: RootState) =>
  select(state).submitApprovalLetterByAmManager ?? initialState.submitApprovalLetterByAmManager;
const selectUpdateUdbtfApprovalStatusSlice = (state: RootState) =>
  select(state).updateUdbtfApprovalStatus ?? initialState.updateUdbtfApprovalStatus;
const selectUpdateUoApprovalStatusSlice = (state: RootState) =>
  select(state).updateUoApprovalStatus ?? initialState.updateUoApprovalStatus;
const selectUpdateUmvnApprovalStatusSlice = (state: RootState) =>
  select(state).updateUmvnApprovalStatus ?? initialState.updateUmvnApprovalStatus;
const selectUdbtfTemplateDocumentSlice = (state: RootState) =>
  select(state).udbtfTemplateDocument ?? initialState.udbtfTemplateDocument;
const selectAmTemplateDocumentSlice = (state: RootState) =>
  select(state).amTemplateDocument ?? initialState.amTemplateDocument;
const selectConclusionTemplateDocumentSlice = (state: RootState) =>
  select(state).conclusionTemplateDocument ?? initialState.conclusionTemplateDocument;
const selectApprovalCommentSlice = (state: RootState) =>
  select(state).approvalComment ?? initialState.approvalComment;
const selectGenerateConclusionAttachmentSlice = (state: RootState) =>
  select(state).generateConclusionAttachment ?? initialState.generateConclusionAttachment;
const selectApplicationFileCapabilitiesSlice = (state: RootState) =>
  select(state).applicationFileCapabilities ?? initialState.applicationFileCapabilities;
const selectApplicationFileTypeCapabilitiesSlice = (state: RootState) =>
  select(state).applicationFileTypeCapabilities ?? initialState.applicationFileTypeCapabilities;
const selectUploadApplicationFileSlice = (state: RootState) =>
  select(state).uploadApplicationFile ?? initialState.uploadApplicationFile;
const selectRegenerateApplicationFileSlice = (state: RootState) =>
  select(state).regenerateApplicationFile ?? initialState.regenerateApplicationFile;
const selectPatchApplicationFileCapabilitiesSlice = (state: RootState) =>
  select(state).patchApplicationFileCapabilities ?? initialState.patchApplicationFileCapabilities;
const selectDownloadApplicationFileSlice = (state: RootState) =>
  select(state).downloadApplicationFile ?? initialState.downloadApplicationFile;
const selectDeleteApplicationFileSlice = (state: RootState) =>
  select(state).deleteApplicationFile ?? initialState.deleteApplicationFile;
export const selectGuaranteesApplications = (state: RootState) =>
  select(state).applications.data.applications ?? [];

export const selectGuaranteesApplicationsPageConfiguration = (state: RootState) =>
  select(state).applications.data.pageConfiguration ?? {
    page: 0,
    size: 10,
    totalElements: 0,
  };

export const selectGuaranteesApplicationsStatus = (state: RootState) =>
  select(state).applications.status;

export const selectGuaranteesApplicationsError = (state: RootState) =>
  select(state).applications.error;

export const selectGuaranteesManagerSupervisorInfo = (state: RootState) =>
  select(state).managerSupervisorInfo.data;

export const selectGuaranteesManagerSupervisorInfoStatus = (state: RootState) =>
  select(state).managerSupervisorInfo.status;
export const selectGuaranteesManagerId = (state: RootState) =>
  selectGuaranteesManagerSupervisorInfo(state)?.managerId ?? '';

export const selectHasGuaranteesPmAccess = (state: RootState) =>
  !!selectGuaranteesManagerSupervisorInfo(state)?.hasPmAccess;

export const selectHasGuaranteesAmAccess = (state: RootState) =>
  !!selectGuaranteesManagerSupervisorInfo(state)?.hasAmAccess;

export const selectHasGuaranteesManagerAccess = (state: RootState) =>
  selectHasGuaranteesPmAccess(state) || selectHasGuaranteesAmAccess(state);

export const selectGuaranteesUakdoSupervisorInfo = (state: RootState) =>
  select(state).uakdoSupervisorInfo.data;

export const selectGuaranteesUakdoSupervisorInfoStatus = (state: RootState) =>
  select(state).uakdoSupervisorInfo.status;

export const selectHasGuaranteesUakdoResponsibleAccess = (state: RootState) =>
  !!selectGuaranteesUakdoSupervisorInfo(state)?.hasResponsibleAccess;

export const selectHasGuaranteesUakdoControllerAccess = (state: RootState) =>
  !!selectGuaranteesUakdoSupervisorInfo(state)?.hasControllerAccess;

export const selectHasGuaranteesAnyUakdoAccess = (state: RootState) =>
  selectHasGuaranteesUakdoResponsibleAccess(state) ||
  selectHasGuaranteesUakdoControllerAccess(state);

const selectGuaranteesAdministrationSupervisorInfoSlice = (
  state: RootState,
  pageKey: TSupervisorAdministrationPageKey
) =>
  select(state).administrationSupervisorInfoByPage?.[pageKey] ??
  initialState.administrationSupervisorInfoByPage[pageKey];

export const selectGuaranteesAdministrationSupervisorInfoByPage = (state: RootState) =>
  select(state).administrationSupervisorInfoByPage ??
  initialState.administrationSupervisorInfoByPage;

export const selectHasGuaranteesAdministrationAccess = (
  state: RootState,
  pageKey: TSupervisorAdministrationPageKey
) =>
  !!selectGuaranteesAdministrationSupervisorInfoSlice(state, pageKey).data?.hasAccess;

export const selectGuaranteesAdministrationSupervisorInfoStatus = (
  state: RootState,
  pageKey: TSupervisorAdministrationPageKey
) => selectGuaranteesAdministrationSupervisorInfoSlice(state, pageKey).status;

export const selectShouldFetchGuaranteesAdministrationSupervisorInfo = (
  state: RootState,
  pageKey: TSupervisorAdministrationPageKey
) =>
  selectGuaranteesAdministrationSupervisorInfoSlice(state, pageKey).status ===
  GuaranteesFetchStatus.Idle;

export const selectGuaranteesAdministrationAccessMap = (state: RootState) => {
  const administrationSupervisorInfoByPage =
    select(state).administrationSupervisorInfoByPage ??
    initialState.administrationSupervisorInfoByPage;

  return Object.fromEntries(
    Object.entries(administrationSupervisorInfoByPage).map(([pageKey, pageState]) => [
      pageKey,
      !!pageState.data?.hasAccess,
    ])
  ) as Record<TSupervisorAdministrationPageKey, boolean>;
};
export const selectGuaranteesAdministrationRoles = (state: RootState) =>
  select(state).administrationRoles.data.roles ?? [];

export const selectGuaranteesAdministrationRolesStatus = (state: RootState) =>
  select(state).administrationRoles.status;
export const selectGuaranteesAdministrationTasks = (state: RootState) =>
  select(state).administrationTasks.data.tasks ?? [];

export const selectGuaranteesAdministrationTasksPageConfiguration = (state: RootState) =>
  select(state).administrationTasks.data.pageConfiguration ?? {
    page: 0,
    size: 5,
    totalElements: 0,
  };

export const selectGuaranteesAdministrationTasksStatus = (state: RootState) =>
  select(state).administrationTasks.status;

export const selectGuaranteesAdministrationTasksError = (state: RootState) =>
  select(state).administrationTasks.error;

export const selectGuaranteesUpdateApplicationStatusStatus = (state: RootState) =>
  select(state).updateApplicationStatus.status;

export const selectGuaranteesTaskSupervisors = (state: RootState) =>
  select(state).taskSupervisors?.data ?? initialState.taskSupervisors.data;

export const selectGuaranteesTaskSupervisorsStatus = (state: RootState) =>
  select(state).taskSupervisors?.status ?? initialState.taskSupervisors.status;

export const selectGuaranteesTaskSupervisorsError = (state: RootState) =>
  select(state).taskSupervisors?.error ?? initialState.taskSupervisors.error;

export const selectGuaranteesPatchApplicationAssigneeStatus = (state: RootState) =>
  select(state).patchApplicationAssignee?.status ??
  initialState.patchApplicationAssignee.status;

export const selectGuaranteesPatchApplicationAssigneeError = (state: RootState) =>
  select(state).patchApplicationAssignee?.error ?? initialState.patchApplicationAssignee.error;

export const selectGuaranteesUpdateUdbtfApprovalStatusStatus = (state: RootState) =>
  selectUpdateUdbtfApprovalStatusSlice(state).status;

export const selectGuaranteesUpdateUoApprovalStatusStatus = (state: RootState) =>
  selectUpdateUoApprovalStatusSlice(state).status;

export const selectGuaranteesUpdateUmvnApprovalStatusStatus = (state: RootState) =>
  selectUpdateUmvnApprovalStatusSlice(state).status;

export const selectGuaranteesSubmitApplicationByManagerStatus = (state: RootState) =>
  selectSubmitApplicationByManagerSlice(state).status;

export const selectGuaranteesSubmitApplicationByManagerError = (state: RootState) =>
  selectSubmitApplicationByManagerSlice(state).error;

export const selectGuaranteesSubmitApprovalLetterByClientStatus = (state: RootState) =>
  selectSubmitApprovalLetterByClientSlice(state).status;

export const selectGuaranteesSubmitApprovalLetterByClientError = (state: RootState) =>
  selectSubmitApprovalLetterByClientSlice(state).error;

export const selectGuaranteesApplicationTimeline = (state: RootState) =>
  select(state).applicationTimeline.data ?? [];

export const selectGuaranteesApplicationTimelineStatus = (state: RootState) =>
  select(state).applicationTimeline.status;

export const selectGuaranteesApplicationFileCapabilities = (state: RootState) =>
  selectApplicationFileCapabilitiesSlice(state).data.fileTypes ?? [];

export const selectGuaranteesApplicationFileCapabilitiesStatus = (state: RootState) =>
  selectApplicationFileCapabilitiesSlice(state).status;

export const selectGuaranteesApplicationFileCapabilitiesError = (state: RootState) =>
  selectApplicationFileCapabilitiesSlice(state).error;

export const selectGuaranteesApplicationFileTypeCapabilities = (state: RootState) =>
  selectApplicationFileTypeCapabilitiesSlice(state).data.fileTypes ?? [];

export const selectGuaranteesApplicationFileTypeCapabilitiesStatus = (state: RootState) =>
  selectApplicationFileTypeCapabilitiesSlice(state).status;

export const selectGuaranteesApplicationFileTypeCapabilitiesError = (state: RootState) =>
  selectApplicationFileTypeCapabilitiesSlice(state).error;

export const selectGuaranteesUploadApplicationFileStatus = (state: RootState) =>
  selectUploadApplicationFileSlice(state).status;

export const selectGuaranteesUploadApplicationFileError = (state: RootState) =>
  selectUploadApplicationFileSlice(state).error;

export const selectGuaranteesRegenerateApplicationFileStatus = (state: RootState) =>
  selectRegenerateApplicationFileSlice(state).status;

export const selectGuaranteesRegenerateApplicationFileError = (state: RootState) =>
  selectRegenerateApplicationFileSlice(state).error;

export const selectGuaranteesPatchApplicationFileCapabilitiesStatus = (state: RootState) =>
  selectPatchApplicationFileCapabilitiesSlice(state).status;

export const selectGuaranteesPatchApplicationFileCapabilitiesError = (state: RootState) =>
  selectPatchApplicationFileCapabilitiesSlice(state).error;

export const selectGuaranteesDownloadApplicationFile = (state: RootState) =>
  selectDownloadApplicationFileSlice(state).data;

export const selectGuaranteesDownloadApplicationFileStatus = (state: RootState) =>
  selectDownloadApplicationFileSlice(state).status;

export const selectGuaranteesDownloadApplicationFileError = (state: RootState) =>
  selectDownloadApplicationFileSlice(state).error;

export const selectGuaranteesDeleteApplicationFileStatus = (state: RootState) =>
  selectDeleteApplicationFileSlice(state).status;

export const selectGuaranteesDeleteApplicationFileError = (state: RootState) =>
  selectDeleteApplicationFileSlice(state).error;

export const selectGuaranteesCreateApplicationStatus = (state: RootState) =>
  select(state).createApplication.status;

export const selectGuaranteesCurrentApplication = (state: RootState) =>
  select(state).currentApplication.data;

export const selectGuaranteesCurrentApplicationStatus = (state: RootState) =>
  select(state).currentApplication.status;

export const selectGuaranteesCurrentApplicationError = (state: RootState) =>
  select(state).currentApplication.error;

export const selectGuaranteesUdbtfTemplateDocument = (state: RootState) =>
  selectUdbtfTemplateDocumentSlice(state).data;

export const selectGuaranteesUdbtfTemplateDocumentStatus = (state: RootState) =>
  selectUdbtfTemplateDocumentSlice(state).status;

export const selectGuaranteesUdbtfTemplateDocumentError = (state: RootState) =>
  selectUdbtfTemplateDocumentSlice(state).error;

export const selectGuaranteesAmTemplateDocument = (state: RootState) =>
  selectAmTemplateDocumentSlice(state).data;

export const selectGuaranteesAmTemplateDocumentStatus = (state: RootState) =>
  selectAmTemplateDocumentSlice(state).status;

export const selectGuaranteesAmTemplateDocumentError = (state: RootState) =>
  selectAmTemplateDocumentSlice(state).error;

export const selectGuaranteesConclusionTemplateDocument = (state: RootState) =>
  selectConclusionTemplateDocumentSlice(state).data;

export const selectGuaranteesConclusionTemplateDocumentStatus = (state: RootState) =>
  selectConclusionTemplateDocumentSlice(state).status;

export const selectGuaranteesConclusionTemplateDocumentError = (state: RootState) =>
  selectConclusionTemplateDocumentSlice(state).error;

export const selectGuaranteesApprovalComment = (state: RootState) =>
  selectApprovalCommentSlice(state).data;

export const selectGuaranteesApprovalCommentStatus = (state: RootState) =>
  selectApprovalCommentSlice(state).status;

export const selectGuaranteesApprovalCommentError = (state: RootState) =>
  selectApprovalCommentSlice(state).error;

export const selectGuaranteesGenerateConclusionAttachmentStatus = (state: RootState) =>
  selectGenerateConclusionAttachmentSlice(state).status;

export const selectGuaranteesGenerateConclusionAttachmentError = (state: RootState) =>
  selectGenerateConclusionAttachmentSlice(state).error;

export const selectGuaranteesSubmitApplicationByUdbtfStatus = (state: RootState) =>
  selectSubmitApplicationByUdbtfSlice(state).status;

export const selectGuaranteesSubmitApplicationByUdbtfError = (state: RootState) =>
  selectSubmitApplicationByUdbtfSlice(state).error;

export const selectGuaranteesSubmitApprovalLetterByUdbtfStatus = (state: RootState) =>
  selectSubmitApprovalLetterByUdbtfSlice(state).status;

export const selectGuaranteesSubmitApprovalLetterByUdbtfError = (state: RootState) =>
  selectSubmitApprovalLetterByUdbtfSlice(state).error;

export const selectGuaranteesSubmitApprovalLetterByUsapokStatus = (state: RootState) =>
  selectSubmitApprovalLetterByUsapokSlice(state).status;

export const selectGuaranteesSubmitApprovalLetterByUsapokError = (state: RootState) =>
  selectSubmitApprovalLetterByUsapokSlice(state).error;

export const selectGuaranteesSubmitApplicationByUakdoStatus = (state: RootState) =>
  selectSubmitApplicationByUakdoSlice(state).status;

export const selectGuaranteesSubmitApplicationByUakdoError = (state: RootState) =>
  selectSubmitApplicationByUakdoSlice(state).error;

export const selectGuaranteesSubmitApplicationByUakdoControllerStatus = (state: RootState) =>
  selectSubmitApplicationByUakdoControllerSlice(state).status;

export const selectGuaranteesSubmitApplicationByUakdoControllerError = (state: RootState) =>
  selectSubmitApplicationByUakdoControllerSlice(state).error;

export const selectGuaranteesRejectApplicationByUakdoControllerStatus = (state: RootState) =>
  selectRejectApplicationByUakdoControllerSlice(state).status;

export const selectGuaranteesRejectApplicationByUakdoControllerError = (state: RootState) =>
  selectRejectApplicationByUakdoControllerSlice(state).error;

export const selectGuaranteesPatchIssueGuaranteeStatus = (state: RootState) =>
  selectPatchIssueGuaranteeSlice(state).status;

export const selectGuaranteesPatchIssueGuaranteeError = (state: RootState) =>
  selectPatchIssueGuaranteeSlice(state).error;

export const selectGuaranteesSubmitApplicationByAmManagerStatus = (state: RootState) =>
  selectSubmitApplicationByAmManagerSlice(state).status;

export const selectGuaranteesSubmitApplicationByAmManagerError = (state: RootState) =>
  selectSubmitApplicationByAmManagerSlice(state).error;

export const selectGuaranteesSubmitApprovalLetterByAmManagerStatus = (state: RootState) =>
  selectSubmitApprovalLetterByAmManagerSlice(state).status;

export const selectGuaranteesSubmitApprovalLetterByAmManagerError = (state: RootState) =>
  selectSubmitApprovalLetterByAmManagerSlice(state).error;

export const selectGuaranteesPatchRootApplicationSectionStatus = (state: RootState) =>
  select(state).patchRootApplicationSection.status;

export const selectGuaranteesPatchRootApplicationSectionError = (state: RootState) =>
  select(state).patchRootApplicationSection.error;

export const selectGuaranteesPrincipal = (state: RootState) =>
  selectPrincipalSlice(state).data;

export const selectGuaranteesPrincipalStatus = (state: RootState) =>
  selectPrincipalSlice(state).status;

export const selectGuaranteesPrincipalError = (state: RootState) =>
  selectPrincipalSlice(state).error;

export const selectGuaranteesPatchPrincipalStatus = (state: RootState) =>
  selectPatchPrincipalSlice(state).status;

export const selectGuaranteesPatchPrincipalError = (state: RootState) =>
  selectPatchPrincipalSlice(state).error;

export const selectGuaranteesBeneficiary = (state: RootState) =>
  selectBeneficiarySlice(state).data;

export const selectGuaranteesBeneficiaryStatus = (state: RootState) =>
  selectBeneficiarySlice(state).status;

export const selectGuaranteesBeneficiaryError = (state: RootState) =>
  selectBeneficiarySlice(state).error;

export const selectGuaranteesPatchBeneficiaryStatus = (state: RootState) =>
  selectPatchBeneficiarySlice(state).status;

export const selectGuaranteesPatchBeneficiaryError = (state: RootState) =>
  selectPatchBeneficiarySlice(state).error;

export const selectGuaranteesBaseRelations = (state: RootState) =>
  selectBaseRelationsSlice(state).data;

export const selectGuaranteesBaseRelationsStatus = (state: RootState) =>
  selectBaseRelationsSlice(state).status;

export const selectGuaranteesBaseRelationsError = (state: RootState) =>
  selectBaseRelationsSlice(state).error;

export const selectGuaranteesPatchBaseRelationsStatus = (state: RootState) =>
  selectPatchBaseRelationsSlice(state).status;

export const selectGuaranteesPatchBaseRelationsError = (state: RootState) =>
  selectPatchBaseRelationsSlice(state).error;

export const selectGuaranteesForm = (state: RootState) => selectGuaranteeFormSlice(state).data;

export const selectGuaranteesFormStatus = (state: RootState) =>
  selectGuaranteeFormSlice(state).status;

export const selectGuaranteesFormError = (state: RootState) =>
  selectGuaranteeFormSlice(state).error;

export const selectGuaranteesPatchFormStatus = (state: RootState) =>
  selectPatchGuaranteeFormSlice(state).status;

export const selectGuaranteesPatchFormError = (state: RootState) =>
  selectPatchGuaranteeFormSlice(state).error;

export const selectGuaranteesMainDealConditions = (state: RootState) =>
  selectMainDealConditionsSlice(state).data;

export const selectGuaranteesMainDealConditionsStatus = (state: RootState) =>
  selectMainDealConditionsSlice(state).status;

export const selectGuaranteesMainDealConditionsError = (state: RootState) =>
  selectMainDealConditionsSlice(state).error;

export const selectGuaranteesPatchMainDealConditionsStatus = (state: RootState) =>
  selectPatchMainDealConditionsSlice(state).status;

export const selectGuaranteesPatchMainDealConditionsError = (state: RootState) =>
  selectPatchMainDealConditionsSlice(state).error;

export const selectGuaranteesManagerTerms = (state: RootState) =>
  selectManagerTermsSlice(state).data;

export const selectGuaranteesManagerTermsStatus = (state: RootState) =>
  selectManagerTermsSlice(state).status;

export const selectGuaranteesManagerTermsError = (state: RootState) =>
  selectManagerTermsSlice(state).error;

export const selectGuaranteesPatchManagerTermsStatus = (state: RootState) =>
  selectPatchManagerTermsSlice(state).status;

export const selectGuaranteesPatchManagerTermsError = (state: RootState) =>
  selectPatchManagerTermsSlice(state).error;

export const selectGuaranteesExecutionForm = (state: RootState) =>
  selectExecutionFormSlice(state).data;

export const selectGuaranteesExecutionFormStatus = (state: RootState) =>
  selectExecutionFormSlice(state).status;

export const selectGuaranteesExecutionFormError = (state: RootState) =>
  selectExecutionFormSlice(state).error;

export const selectGuaranteesPatchExecutionFormStatus = (state: RootState) =>
  selectPatchExecutionFormSlice(state).status;

export const selectGuaranteesPatchExecutionFormError = (state: RootState) =>
  selectPatchExecutionFormSlice(state).error;

export const selectGuaranteesPatchApplicationTenderStatus = (state: RootState) =>
  select(state).patchApplicationTender.status;

export const selectGuaranteesPatchApplicationTenderError = (state: RootState) =>
  select(state).patchApplicationTender.error;

export const selectGuaranteesApplicationTenderInfo = (state: RootState) =>
  selectApplicationTenderInfoSlice(state).data;

export const selectGuaranteesApplicationTenderInfoStatus = (state: RootState) =>
  selectApplicationTenderInfoSlice(state).status;

export const selectGuaranteesApplicationTenderInfoError = (state: RootState) =>
  selectApplicationTenderInfoSlice(state).error;

export const selectGuaranteesFetchProzorroTender = (state: RootState) =>
  select(state).fetchProzorroTender.data;

export const selectGuaranteesFetchProzorroTenderStatus = (state: RootState) =>
  select(state).fetchProzorroTender.status;

export const selectGuaranteesFetchProzorroTenderError = (state: RootState) =>
  select(state).fetchProzorroTender.error;

export const selectGuaranteesMainDeals = (state: RootState) =>
  select(state).mainDeals.data.content ?? [];

export const selectGuaranteesMainDealsStatus = (state: RootState) =>
  select(state).mainDeals.status;

export const selectGuaranteesMainDealsError = (state: RootState) =>
  select(state).mainDeals.error;

export const selectGuaranteesMainDealsRequestKey = (state: RootState) =>
  select(state).mainDealsRequestKey;

export const selectGuaranteesPatchApplicationMainDealStatus = (state: RootState) =>
  select(state).patchApplicationMainDeal.status;

export const selectGuaranteesPatchApplicationMainDealError = (state: RootState) =>
  select(state).patchApplicationMainDeal.error;

export const selectGuaranteesAccounts = (state: RootState) =>
  select(state).accounts.data.content ?? [];

export const selectGuaranteesAccountsStatus = (state: RootState) =>
  select(state).accounts.status;

export const selectGuaranteesAccountsError = (state: RootState) =>
  select(state).accounts.error;

export const selectGuaranteesPrincipalDebitAccounts = (state: RootState) =>
  select(state).principalDebitAccounts?.data.content ??
  initialState.principalDebitAccounts.data.content ??
  [];

export const selectGuaranteesPrincipalDebitAccountsStatus = (state: RootState) =>
  select(state).principalDebitAccounts?.status ?? initialState.principalDebitAccounts.status;

export const selectGuaranteesPrincipalDebitAccountsError = (state: RootState) =>
  select(state).principalDebitAccounts?.error ?? initialState.principalDebitAccounts.error;

export const selectGuaranteesPatchApplicationSecretStatus = (state: RootState) =>
  select(state).patchApplicationSecret.status;

export const selectGuaranteesPatchApplicationSecretError = (state: RootState) =>
  select(state).patchApplicationSecret.error;

export const selectGuaranteesSecurityCurrencies = (state: RootState) =>
  select(state).securityCurrencies?.data?.currencies ??
  select(state).currentApplication.data?.security?.currencies ??
  [];

export const selectGuaranteesSecurityCurrenciesStatus = (state: RootState) =>
  select(state).securityCurrencies?.status ?? initialState.securityCurrencies.status;

export const selectGuaranteesSecurityCurrenciesError = (state: RootState) =>
  select(state).securityCurrencies?.error ?? initialState.securityCurrencies.error;

export const selectGuaranteesPutSecurityCurrenciesStatus = (state: RootState) =>
  select(state).putSecurityCurrencies?.status ?? initialState.putSecurityCurrencies.status;

export const selectGuaranteesPutSecurityCurrenciesError = (state: RootState) =>
  select(state).putSecurityCurrencies?.error ?? initialState.putSecurityCurrencies.error;

export const selectGuaranteesCheckApplicationRequestStatus = (state: RootState) =>
  select(state).checkApplicationRequest.status;

export const selectGuaranteesCheckApplicationRequestError = (state: RootState) =>
  select(state).checkApplicationRequest.error;

const selectGuaranteesSupervisorSegmentsByGroupSlice = (
  state: RootState,
  groupKey: TGuaranteesSupervisorGroupKey
) =>
  select(state).supervisorSegmentsByGroup?.[groupKey] ??
  initialState.supervisorSegmentsByGroup[groupKey];

export const selectGuaranteesSupervisorSegmentsByGroup = (
  state: RootState,
  groupKey: TGuaranteesSupervisorGroupKey
) => selectGuaranteesSupervisorSegmentsByGroupSlice(state, groupKey).data ?? [];

export const selectGuaranteesSupervisorSegmentsByGroupStatus = (
  state: RootState,
  groupKey: TGuaranteesSupervisorGroupKey
) => selectGuaranteesSupervisorSegmentsByGroupSlice(state, groupKey).status;

export const selectGuaranteesSupervisorSegmentsByGroupError = (
  state: RootState,
  groupKey: TGuaranteesSupervisorGroupKey
) => selectGuaranteesSupervisorSegmentsByGroupSlice(state, groupKey).error;
